/**
 * Knowledge Base for Farm Manager AI
 *
 * Contains structured domain knowledge for calf health management, health protocols,
 * troubleshooting guides, and BioCV feature capabilities.
 * Supports multi-language content (English/German).
 */

const KNOWLEDGE_BASE = {
        en: {
                livestockManagement: {
                        title: "Calf Management Best Practices",
                        content: `
Health Score Interpretation:
- Health scores range from 0-100, calculated from sensor data (activity patterns, movement, resting periods, feeding behavior), treatment history, and behavioral indicators
- BioCV continuously collects data from sensors (via BioNodes) tracking activity, movement patterns, location, and physiological parameters
- Scores below 35 indicate animals requiring immediate attention (suspicious status)
- Scores 50-70 suggest monitoring needed, potential health concerns
- Scores 70-85 indicate normal health status
- Scores 85-100 represent excellent health and optimal conditions
- Health scores are dynamic and update based on recent activity and sensor readings

Activity Anomaly Detection:
- BioCV learns normal activity patterns for each calf and detects significant deviations
- Unusual patterns include: too little movement, excessive activity, abnormal resting periods
- Activity anomalies can be early indicators for illness, stress, or environmental problems
- The system compares current activity against learned baseline patterns

Location and Group Behavior Tracking:
- Sensors track where calves are in the barn and how they interact within their group
- Changes in group behavior or calves isolating themselves can indicate health issues
- Location tracking helps identify calves that have moved away from their normal areas

Sensor Data Trend Analysis:
- BioCV visualizes historical sensor data over days, weeks, or months
- Trend analysis helps identify gradual problems early or evaluate treatment effectiveness
- Monitor how activity, health scores, or other parameters develop over time
- Use animal-details tool to retrieve all available information and historical data for specific calves

Calf Grouping Best Practices:
- Group calves by lifecycle stage (newborn, pre-weaning, weaning, post-weaning, grower)
- Organize by age cohorts for better health monitoring
- Separate sick or recovering calves into dedicated groups
- Use location-based grouping for barn section management
- Maintain consistent group assignments for tracking patterns over time

Activity Monitoring:
- Normal activity patterns vary by age, breed, and lifecycle stage
- Sudden decreases in activity may indicate illness, injury, or stress
- Increased restlessness can signal discomfort, pain, or environmental issues
- Monitor feeding patterns - reduced milk/feed intake is an early health indicator
- Track activity trends over time to identify subtle changes before clinical signs appear
`
                },
                healthIssues: {
                        title: "Common Calf Health Issues and Symptoms",
                        content: `
Early Warning Signs:
- Health score drops below 35 (suspicious flag triggered)
- Reduced activity compared to baseline patterns
- Decreased milk/feed intake or water consumption
- Isolation from group or reluctance to move
- Abnormal posture or gait (lameness indicators)
- Changes in respiratory patterns or coughing
- Droopy ears, nasal discharge, watery eyes

Bovine Respiratory Disease (BRD):
- Most common and costly calf disease
- BioCV AI can detect respiratory distress through activity pattern changes
- Wisconsin scoring system: eyes (0-3), ears (0-3), nasal (0-3), cough (0-3)
- Total Wisconsin score ≥5 indicates likely BRD requiring treatment
- Early intervention significantly improves outcomes and reduces mortality
- Monitor calves with respiratory predictions closely

Neonatal Calf Diarrhea (Scours):
- Leading cause of death in calves under 30 days
- California faecal scoring: 0 (normal) to 3 (watery/severe)
- Score ≥2 requires intervention (fluid therapy, electrolytes)
- Common causes: E.coli, rotavirus, coronavirus, cryptosporidium, coccidia
- Dehydration is the primary cause of death - early rehydration critical

Navel Ill / Joint Ill:
- Infection entering through the umbilicus in first days of life
- Swollen, hot, or draining navel; swollen joints; fever; reluctance to stand
- Prevention: proper navel dipping with iodine at birth, clean calving environment

Heat Stress Indicators:
- Elevated body temperature (sensor readings above normal range of 38.5-39.5°C)
- Increased respiratory rate (>40 breaths/min)
- Reduced feed intake and milk consumption
- Lethargy or decreased activity
- Calves seeking shade or water sources
- Monitor environmental sensors for high temperature/humidity combinations
`
                },
                treatmentProtocols: {
                        title: "Treatment Protocols and Management",
                        content: `
Treatment Planning:
- Always consult with veterinarian for diagnosis and treatment protocols
- Use treatment templates for consistency and compliance
- Document all treatments with accurate dates, dosages, and administration routes
- Set reminders for follow-up treatments or rechecks
- Track treatment outcomes to evaluate effectiveness

Medication Best Practices:
- Calculate dosages based on accurate current weight
- Follow withdrawal periods for food-producing animals
- Maintain treatment records for regulatory compliance
- Rotate medications to prevent resistance development
- Store medications according to manufacturer specifications

Treatment Scheduling:
- Prioritize overdue treatments immediately
- Schedule treatments during routine barn checks when possible
- Group treatments by location to improve efficiency
- Set recurring treatments for preventive care (vaccinations, deworming)
- Coordinate with work planner for staff scheduling

Emergency Response:
- Calves with health scores below 30 require immediate attention
- Multiple calves with sudden score drops may indicate environmental issue or outbreak
- Coordinate with veterinarian for critical cases
- Isolate affected calves to prevent disease spread
- Document emergency treatments and outcomes
`
                },
                breedingCycle: {
                        title: "Calf Lifecycle Stage Management",
                        content: `
Newborn Stage (0-3 days):
- Colostrum management is the single most critical factor for calf survival
- First feeding within 2 hours of birth, minimum 4 liters of quality colostrum
- Measure colostrum quality with Brix refractometer (>22% Brix = good quality)
- Navel dipping with 7% iodine solution immediately after birth
- Monitor for meconium passage and initial nursing behavior
- Verify passive transfer via serum total protein (>5.5 g/dL) at 24-48 hours

Pre-Weaning Stage (3 days - 8 weeks):
- Consistent milk/milk replacer feeding schedule (2-3 times daily)
- Introduce calf starter grain by day 3-4 to stimulate rumen development
- Monitor daily for scours, respiratory signs, and general alertness
- Target average daily gain (ADG) of 0.7-1.0 kg/day
- Fresh water available at all times from day 1

Weaning Stage (6-12 weeks):
- Gradual weaning when calf consistently eats 1-1.5 kg starter per day
- Step-down weaning over 7-14 days preferred over abrupt weaning
- Monitor for weaning stress: reduced intake, increased vocalization, health dips
- Health scores may temporarily dip during weaning transition - watch for sustained drops

Post-Weaning / Grower Stage (12+ weeks):
- Transition to grower ration
- Group housing with appropriate stocking density
- Continue monitoring growth rate (target ADG based on breed goals)
- Vaccination boosters as per protocol
- Regular weight recording for growth curve tracking
`
                },
                bionodeTroubleshooting: {
                        title: "BioNode Troubleshooting",
                        content: `
Connectivity Issues:
- Check BioNode status using the bionode-status tool to verify connection
- Nodes showing "disconnected" or "offline" require investigation
- Verify network connectivity and power supply
- Check Bluetooth connectivity if using wireless communication
- Restart BioNode if connectivity issues persist

Data Collection Problems:
- Missing or delayed sensor readings may indicate sensor malfunction
- Check animal activity data - gaps suggest communication issues
- Verify BioNode is within range of animal transponders
- Check for physical obstructions affecting signal transmission
- Review recent firmware updates or configuration changes

Sensor Accuracy:
- Temperature and humidity readings should match environmental conditions
- Unexpected sensor values may indicate calibration issues
- Compare readings across multiple nodes for consistency
- Verify sensor placement and environmental factors
- Document any persistent accuracy issues for technical support

Diagnostic Steps:
1. Use bionode-analysis tool to get detailed node diagnostics
2. Check connectivity status and last data transmission time
3. Review recent error logs or warning messages
4. Verify power supply and network connections
5. Test communication with specific animal transponders
6. Contact technical support if issues persist after basic troubleshooting
`
                },
                featureCapabilities: {
                        title: "BioCV CalfHealth Feature Capabilities",
                        content: `
Animal Management:
- Real-time tracking of individual calves by ID or MAC address
- Group management with bulk operations support
- Health score monitoring with automatic suspicious flagging
- Location tracking within barn sections
- Complete lifecycle documentation from birth through all stages

Calf Health Tools:
- Calf lifecycle overview: Track calves across all lifecycle stages (newborn → pre-weaning → weaning → post-weaning → grower → finisher)
- Vaccination status: Monitor vaccination compliance, overdue and upcoming vaccinations
- Weight & growth analysis: Track ADG (Average Daily Gain), detect weight anomalies, compare against breed targets
- Disease overview: Track disease records by category, Wisconsin respiratory scores, California faecal scores
- Calf health KPIs: Mortality rate, ADG, vaccination compliance, disease incidence, scours rate, BRD rate, Wisconsin scores

Treatment Management:
- Treatment templates for common protocols
- Scheduling system with automated reminders
- Overdue treatment alerts and prioritization
- Complete treatment history per calf
- Compliance reporting and documentation

Work Planning:
- Calendar, Kanban, and list views for task management
- Worker assignment and task prioritization
- Recurring task scheduling
- Task dependencies and sequencing
- Mobile notifications for assigned tasks

Sensor Integration:
- Temperature and humidity monitoring (silo sensors, environmental sensors)
- Water consumption tracking (BioFlow water meters)
- Real-time alerts for threshold violations
- Historical data visualization and trend analysis

Data Tools Available:
- Use account-overview for user profile information
- Use treatment-history for active and past treatments
- Use birth-history for birth/arrival records and lifecycle transitions
- Use calf-lifecycle-overview for stage distribution across the herd
- Use vaccination-status for vaccination compliance tracking
- Use weight-growth-analysis for ADG and growth monitoring
- Use disease-overview for disease record analysis and Wisconsin scores
- Use calf-health-kpis for comprehensive herd health KPIs
- Use work-planner-overview for task management
- Use animal-details for comprehensive calf information
- Use bionode-status and bionode-analysis for device diagnostics
- Use farm-kpi-overview for herd KPI snapshots
- Use farm-kpi-trends to compare KPI changes over time
`
                },
                productionData: {
                        title: "Calf Production Data Reference",
                        content: `
Scope and Data Types:
- Covers complete calf lifecycle from birth/arrival through all growth stages
- ERP collections include Births, Lifecycle Events, Vaccinations, Weight Records, Disease Records, Treatments, and General Events
- Lifecycle stages: newborn, pre-weaning, weaning, post-weaning, grower, finisher, sold, deceased

Key Record Details:
- Birth records store date, dam ID, birth weight, sex, calving difficulty, and comments
- Lifecycle events capture stage transitions with timestamps and notes
- Weight records track growth over time, enabling ADG calculations and anomaly detection
- Vaccination records track protocol compliance, due dates, and administration details
- Disease records include Wisconsin respiratory scores, California faecal scores, diagnoses, and outcomes
- Documents are stored at Users/{userId}/{Collection}/{timestamp} with t = milliseconds and MAC/UId linking to the animal

CSV Import Workflow:
1. Upload CSV and select record type (delimiter, encoding, and headers detected automatically)
2. Map required columns with smart suggestions
3. Preview transformed rows with validation (animal existence, date/time format, required fields)
4. Import batches with progress tracking—successful records persist even if others fail

Operational Tips:
- Register animals and standardize IDs before bulk imports to avoid lookup errors
- Use ISO, US, or EU date formats and HH:mm(/:ss) times; keep CSV headers descriptive
- Split large historical datasets into batches of ~200 rows and verify summaries after each run
- Document birth weights, dam information, and calving difficulty for richer analytics
`
                },
                operationalGuidance: {
                        title: "Operational Guidance",
                        content: `
Daily Operations Checklist:
1. Review priority alerts and suspicious calves (health scores < 35)
2. Check overdue treatments and vaccinations, schedule urgent interventions
3. Monitor newborn calves for colostrum intake and vigor
4. Check for signs of scours in pre-weaning calves
5. Review work planner for today's scheduled tasks
6. Review sensor readings for environmental anomalies
7. Verify BioNode connectivity and data collection

Weekly Review:
- Analyze health score trends across herd
- Review treatment outcomes and adjust protocols as needed
- Assess vaccination compliance and schedule upcoming vaccinations
- Evaluate group performance and consider regrouping
- Review weight gain data and ADG trends
- Check device status and address any connectivity issues

Monthly Analysis:
- Generate production reports and performance metrics (mortality, ADG, disease incidence)
- Review treatment costs and medication usage
- Analyze Wisconsin and California scores for disease trends
- Evaluate calf growth curves against breed targets
- Review sensor calibration and accuracy
- Plan for upcoming weaning transitions and group movements

Data-Driven Decision Making:
- Always ground recommendations in actual farm data
- Use tool calls to fetch precise information before advising
- Compare current data to historical patterns
- Identify trends before they become problems
- Leverage health monitoring data to optimize timing of interventions
`
                },
                diseaseProfiles: {
                        title: "Calf Disease Diagnostics and Management",
                        content: `
RESPIRATORY DISEASES:

Bovine Respiratory Disease Complex (BRD):
- Most common and economically significant calf disease
- Causes: Mannheimia haemolytica, Pasteurella multocida, Histophilus somni, BRSV, IBR, PI3, BVD
- Symptoms: Cough, nasal discharge, fever (>39.5°C), droopy ears, reduced feed intake, depression
- Sensor patterns: Sudden activity drop (30-50% below baseline), reduced feeding activity, isolation from group
- Health score impact: Gradual decline over 2-4 days, scores typically fall below 40
- BioCV detection: Look for multiple calves in same group showing simultaneous score drops
- Wisconsin Scoring System:
  * Eyes: 0=normal, 1=mild discharge, 2=moderate bilateral, 3=heavy discharge/crusting
  * Ears: 0=normal, 1=ear flick/head shake, 2=unilateral droop, 3=bilateral droop
  * Nasal: 0=normal, 1=small unilateral, 2=bilateral/cloudy, 3=copious mucopurulent
  * Cough: 0=none, 1=single induced, 2=repeated spontaneous, 3=repeated wet cough
  * Total ≥5 = treat for BRD
- Action: Isolate, treat with appropriate antimicrobial per vet protocol, NSAID for fever/pain
- Prevention: Proper ventilation, colostrum management, vaccination program, avoid overcrowding

Pneumonia (Bacterial / Viral):
- Symptoms: Chronic cough, labored breathing, poor growth rates, occasional fever
- Sensor patterns: Irregular activity patterns, increased solitary behavior, reduced feeding activity
- Health score impact: Moderate decline (50-65), prolonged recovery curve over weeks
- Action: Antibiotic treatment per veterinary protocol, improve ventilation, reduce stocking density

DIGESTIVE DISORDERS:

Neonatal Calf Diarrhea (Scours):
- Leading cause of calf mortality in first 30 days of life
- Causes by age:
  * Day 1-3: E.coli (enterotoxigenic), Clostridium perfringens
  * Day 3-21: Rotavirus, Coronavirus, Cryptosporidium
  * Day 7-28: Cryptosporidium, Coccidia, Salmonella
- California Faecal Scoring:
  * 0 = Normal (firm but not hard)
  * 1 = Semi-formed, pasty
  * 2 = Loose but stays on top of surface
  * 3 = Watery, sifts through surface
  * Score ≥2 = intervention needed
- Sensor patterns: Reduced nursing/feeding activity, lethargy, dehydration indicators
- Health score impact: Rapid decline, especially dangerous in calves under 14 days
- Action: Oral electrolytes (do NOT stop milk feeding), IV fluids for severe dehydration, identify pathogen
- Prevention: Proper colostrum intake, clean calving environment, dam vaccination (scours vaccine)
- Critical: Dehydration kills faster than the pathogen - rehydrate first, treat cause second

Bloat / Ruminal Acidosis:
- Symptoms: Distended left flank, discomfort, reduced appetite, grinding teeth
- Causes: Grain overload, poor milk replacer mixing, fermented milk
- Action: Veterinary intervention for severe bloat, adjust feeding program
- Prevention: Gradual diet transitions, proper milk replacer preparation

NAVEL AND JOINT INFECTIONS:

Navel Ill (Omphalitis):
- Infection of the umbilicus, usually within first week of life
- Symptoms: Swollen, hot, or draining navel; fever; poor nursing
- Can lead to: Liver abscess, joint ill, septicemia
- Action: Antibiotic treatment, drain if abscess present, supportive care
- Prevention: Dip navel with 7% iodine at birth, clean dry calving area, ensure colostrum intake

Joint Ill (Septic Arthritis):
- Often secondary to navel infection or bacteremia
- Symptoms: Swollen hot joints, lameness, fever, reluctance to stand
- Action: Aggressive antibiotic therapy, NSAID, joint lavage in severe cases
- Prevention: Good navel care, adequate colostrum, clean environment

DISEASE SURVEILLANCE WITH BIOCV:

Early Warning Indicators:
1. Group health score drops >5 points average across 24 hours = investigate
2. 3+ calves in same location showing simultaneous changes = potential outbreak
3. Activity patterns deviating >30% from group baseline = individual attention needed
4. Environmental sensors showing temp/humidity extremes = stress risk

Investigation Protocol:
1. Use animal-details to review history and sensor patterns
2. Check treatment-history for recent interventions
3. Use disease-overview for Wisconsin and California scores
4. Cross-reference with environmental sensor data
5. Document findings for veterinary consultation

WHEN TO CALL THE VET IMMEDIATELY:
- Multiple calves with sudden severe symptoms
- Health scores below 30 with rapid decline
- Severe dehydration (sunken eyes, skin tenting >3 seconds)
- Neurological symptoms (circling, tremors, paralysis)
- Sudden deaths with no obvious cause
- Suspected notifiable disease
`
                },
                nutritionGuidance: {
                        title: "Calf Nutrition and Feeding Management",
                        content: `
FEEDING BY LIFECYCLE STAGE:

Newborn (Day 0-3):
- Colostrum is the #1 priority
  * First feeding: 4 liters within 2 hours of birth (10% of body weight)
  * Second feeding: 2-3 liters within 6-12 hours
  * Quality: >22% Brix refractometer reading = good quality
  * Passive transfer check: Serum total protein >5.5 g/dL at 24-48 hours
- Failed passive transfer (FPT) increases disease risk 3-5x
- Colostrum can be frozen and banked for emergencies

Pre-Weaning (Day 3 to 6-8 weeks):
- Whole milk or milk replacer (MR):
  * 4-6 liters/day split into 2-3 feedings
  * MR: minimum 20% protein, 20% fat for conventional programs
  * Accelerated programs: 26-28% protein, 15-20% fat, 6-8 liters/day
- Calf starter grain: Introduce from day 3-4
  * 18-20% crude protein
  * Start with small amounts, increase as intake grows
  * Target: 1-1.5 kg/day starter intake before weaning
- Fresh clean water: Available from day 1
  * Critical for rumen development and starter intake
- Target ADG: 0.7-1.0 kg/day (accelerated: 0.9-1.2 kg/day)

Weaning Transition (6-12 weeks):
- Ready to wean when eating 1-1.5 kg starter consistently for 3 consecutive days
- Step-down method: Reduce milk gradually over 7-14 days
  * Example: 6L → 4L → 2L → 0L over 10 days
- Monitor weight and health closely during transition
- Ensure adequate water intake increases as milk decreases
- Health scores may dip temporarily - watch for sustained drops vs. normal adjustment

Post-Weaning / Grower:
- Transition to total mixed ration (TMR) or grower pellet
- Introduce forage gradually (high quality hay)
- Monitor growth rates and feed conversion
- Target ADG varies by breed and production goals

BODY CONDITION SCORING FOR CALVES:

Visual Assessment:
- Monitor overall body fill, ribs, and topline
- Calves should have smooth sides without protruding ribs or hip bones
- Sunken flanks indicate dehydration or insufficient feeding
- Pot-bellied appearance may indicate parasites or poor nutrition
- Regular weighing is more accurate than visual assessment for growth tracking

FEEDING-RELATED HEALTH INDICATORS:

Feed Intake Monitoring:
- Reduced intake is often the FIRST sign of illness
- BioFlow water meters help track consumption patterns
- Sudden intake drops (>30%) warrant investigation
- Consider: illness, heat stress, feed quality, water availability

Temperature Effects on Intake:
- Hot weather (>25°C): Intake decreases, increase water availability
- Cold weather (<5°C): Energy requirements increase 15-30%, increase milk/feed volume
- Adjust feeding during temperature extremes
- Provide shelter and bedding for thermoregulation

WATER REQUIREMENTS:

Daily Water Needs:
- Neonatal calves: 0.5-1 liter/day (in addition to milk)
- Pre-weaning calves: 2-5 liters/day
- Weaning calves: 5-10 liters/day (increases as milk decreases)
- Post-weaning: 10-20+ liters/day (5-10% of body weight)
- Lactating cows: 70-120+ liters/day

Water Quality:
- Test water annually (bacteria, minerals, pH)
- High nitrates: Health and growth issues
- High iron: Palatability problems
- Bacteria: Check troughs and lines regularly
- Clean water troughs daily for young calves

BioFlow Monitoring:
- Establish baseline consumption patterns
- Deviations >20% from baseline = investigate
- Sudden drops may indicate: illness, equipment failure, heat stress
- Seasonal patterns are normal - track year-over-year
`
                },
                economicAnalysis: {
                        title: "Economic Analysis and Calf Health KPIs",
                        content: `
KEY PERFORMANCE INDICATORS (KPIs):

Calf Mortality Rate:
- Definition: (Deaths / Total calves born or entered) × 100
- Target benchmarks:
  * Pre-weaning: <5% (elite: <3%)
  * Post-weaning: <2%
  * Overall: <5%
- High mortality indicates: poor colostrum management, disease pressure, environment issues

Average Daily Gain (ADG):
- Definition: (Current weight - Birth weight) / Age in days
- Target benchmarks by stage:
  * Pre-weaning (whole milk): 0.7-1.0 kg/day
  * Pre-weaning (accelerated): 0.9-1.2 kg/day
  * Post-weaning: 0.8-1.2 kg/day
  * Grower: depends on breed and production goals
- Low ADG indicates: disease, poor nutrition, stress, environmental factors
- Use weight-growth-analysis tool to track individual and herd ADG

Vaccination Compliance:
- Definition: (Vaccinated on time / Total due) × 100
- Target: >95%
- Low compliance increases disease risk and treatment costs
- Use vaccination-status tool to monitor overdue and upcoming vaccinations

Disease Incidence Rate:
- Definition: (New disease cases / Total calves) × 100 in a period
- Track by disease category: respiratory (BRD), digestive (scours), other
- Target: <15% BRD incidence, <20% scours incidence in pre-weaning calves

Scours Rate:
- Definition: (Calves with California faecal score ≥2 / Total calves) × 100
- Target: <15% in pre-weaning calves
- High rates indicate: colostrum failure, hygiene issues, pathogen pressure

BRD Rate:
- Definition: (Calves with Wisconsin score ≥5 / Total calves) × 100
- Target: <10% (elite: <5%)
- High rates indicate: ventilation issues, overcrowding, stress, pathogen exposure

Average Wisconsin Score:
- Herd-level respiratory health indicator
- Target: <3 average across herd
- Rising trend = investigate ventilation, density, and pathogen pressure

COST ANALYSIS:

Treatment Costs:
- Track treatment costs per calf per period
- High treatment costs may indicate: endemic disease, poor prevention, environment issues
- Prevention (vaccination, colostrum, hygiene) typically costs 10-20% of treatment
- Preventive health programs are almost always more economical

Mortality Costs:
- Each calf death = total investment lost (genetics, colostrum, feed, labor, treatments)
- Estimated value per calf at weaning: €200-500+ depending on breed/genetics
- Reducing mortality by 2% in a 200-calf operation saves €800-2000+/year

Growth Performance Costs:
- Each day of sub-optimal ADG represents lost production potential
- Calves that fall behind in early growth rarely catch up fully
- Disease events typically reduce ADG by 0.1-0.3 kg/day during illness and recovery

BENCHMARKING AND ANALYSIS:

Use farm-kpi-trends to:
1. Compare performance across time periods (month-over-month, year-over-year)
2. Identify seasonal patterns (summer heat stress, winter respiratory disease)
3. Track intervention effectiveness (before/after comparisons)
4. Set realistic improvement targets based on historical performance

Use calf-health-kpis for current snapshot of:
- Mortality rate, ADG, vaccination compliance, disease incidence
- Scours rate, BRD rate, average Wisconsin score

CULLING / REMOVAL DECISIONS:

Consider removal when:
- Chronic poor health: 3+ treatment courses in 6 months with no improvement
- Persistent poor growth: ADG consistently <50% of group average
- Chronic lameness or structural issues affecting welfare
- Cost of continued treatment exceeds animal value

REPORTING AND ANALYSIS TOOLS:

Use generate-report tool for:
- weekly-summary: Quick performance overview
- health-overview: Focus on health metrics and concerns
- calf-health: Vaccination, disease, and growth performance summary
- vet-prep: Comprehensive summary for veterinary visits

Use comparative-analysis tool to:
- Compare group performance (e.g., pre-weaning vs. post-weaning cohorts)
- Benchmark against previous periods
- Identify best and worst performing cohorts
`
                },
                troubleshootingDecisions: {
                        title: "Troubleshooting Decision Trees",
                        content: `
DECISION TREE: Calf with Low Health Score (<35)

Step 1: Assess the Decline Pattern
├── SUDDEN DROP (>20 points in 24 hours)?
│   ├── YES → Check for acute illness
│   │   ├── Multiple calves affected simultaneously?
│   │   │   ├── YES → Likely environmental or infectious outbreak
│   │   │   │   ├── Check: Ventilation, temperature, water, feed quality
│   │   │   │   ├── Check: Recent additions to group (disease introduction)
│   │   │   │   └── ACTION: Contact vet, isolate group, investigate environment
│   │   │   └── NO → Individual acute problem
│   │   │       ├── Check: Scours (California score), respiratory signs (Wisconsin score)
│   │   │       └── ACTION: Examine calf, check treatment history, consult vet
│   └── NO → Gradual decline
│       ├── Check treatment history for chronic condition
│       ├── Check activity patterns for behavioral clues
│       │   ├── Reduced movement → Possible lameness, systemic illness, or weakness
│       │   ├── Isolation behavior → Illness or social stress
│       │   └── Reduced feeding → GI issues, scours, or general illness
│       └── ACTION: Schedule examination, review history, monitor closely

Step 2: Review Context
- Lifecycle stage: Newborns have different risk factors than weaning calves
- Recent events: Recent weaning, group change, vaccination can cause temporary drops
- Group context: Is this calf an outlier or part of a pattern?

DECISION TREE: Calf with Scours (Diarrhea)

Step 1: Assess Severity (California Faecal Score)
├── Score 0-1 (Normal/Mild)?
│   └── Monitor closely, ensure adequate water intake
├── Score 2 (Loose)?
│   ├── Check dehydration status (skin tent, eye appearance)
│   ├── Start oral electrolytes between milk feedings
│   ├── Do NOT stop milk feeding
│   └── ACTION: Monitor 2x daily, treat underlying cause if identified
└── Score 3 (Watery/Severe)?
    ├── Check for severe dehydration (sunken eyes, skin tent >3 sec, cold extremities)
    ├── IV fluids if severely dehydrated (veterinary assistance)
    ├── Oral electrolytes between milk feedings
    └── ACTION: Contact vet, isolate calf, intensive monitoring, identify pathogen

Step 2: Identify Likely Cause by Age
├── 0-3 days → E.coli, Clostridium → Check dam vaccination, colostrum intake
├── 3-21 days → Rotavirus, Coronavirus, Crypto → Check hygiene, other calves
└── 7-28+ days → Coccidia, Salmonella, nutritional → Check feed, environment

DECISION TREE: BioNode Connectivity Issues

Step 1: Scope of Problem
├── SINGLE NODE affected?
│   ├── Check power supply
│   │   ├── Power light on? → Power OK, check network
│   │   └── Power light off? → Check outlet, cable, fuse
│   ├── Check physical location
│   │   ├── Any new obstructions? Metal objects, water, equipment
│   │   └── Node moved or damaged?
│   ├── Check network indicators
│   │   ├── Network light on? → Connected to network
│   │   └── Network light off? → Cable issue or switch problem
│   └── ACTION: Power cycle node, check cables, relocate if needed
│
└── MULTIPLE NODES affected?
    ├── Check common infrastructure
    │   ├── Network switch/router functioning?
    │   ├── Internet connection active?
    │   └── Power circuit intact?
    ├── Check for recent changes
    │   ├── Firmware updates?
    │   ├── Network configuration changes?
    │   └── New equipment interference?
    └── ACTION: Restart network equipment, check gateway, contact support

Use bionode-status and bionode-analysis tools to gather diagnostic data before troubleshooting.

DECISION TREE: High Calf Mortality (>5% pre-weaning)

Step 1: Categorize Losses by Timing
├── DEATHS in first 48 hours?
│   ├── Dystocia / calving difficulty → Review calving management
│   ├── Failed passive transfer → Check colostrum protocol
│   │   ├── Was colostrum given within 2 hours? Check volume and quality
│   │   └── Verify passive transfer with serum total protein testing
│   └── Hypothermia/exposure → Check calving environment
│
├── DEATHS in first 2 weeks?
│   ├── Scours → Check pathogen, dam vaccination, hygiene, colostrum quality
│   ├── Navel ill → Check navel dipping protocol, calving area cleanliness
│   └── Septicemia → Check colostrum program, environment
│
└── DEATHS in 2-8 weeks?
    ├── BRD → Check ventilation, stocking density, stress factors
    ├── Persistent scours → Check parasite load, coccidia, chronic infections
    └── Other → Full investigation needed

Use disease-overview and calf-health-kpis to analyze mortality and disease patterns.

GENERAL TROUBLESHOOTING APPROACH:

1. GATHER DATA FIRST
   - Use relevant tools to get current information
   - Don't assume - verify with data

2. LOOK FOR PATTERNS
   - Is this individual or group-wide?
   - Is this sudden or gradual?
   - Is this new or recurring?

3. CHECK THE OBVIOUS
   - Environment (temperature, ventilation, water, bedding)
   - Recent changes (feed, management, personnel, group movements)
   - Equipment function

4. ESCALATE APPROPRIATELY
   - Management issues → Adjust protocols
   - Health concerns → Veterinary consultation
   - Equipment problems → Technical support

5. DOCUMENT AND FOLLOW UP
   - Record findings and actions
   - Schedule follow-up checks
   - Evaluate intervention success
`
                }
        },
        de: {
                livestockManagement: {
                        title: "Best Practices der Kälberhaltung",
                        content: `
Gesundheitsbewertung (Sensor Trend Index) Interpretation:
- Gesundheitswerte reichen von 0-100, berechnet aus Sensordaten, Aktivitätsmustern und Verhaltensindikatoren
- Werte unter 50 zeigen Tiere an, die sofortige Aufmerksamkeit benötigen (verdächtiger Status)
- Werte 50-70 deuten auf notwendige Überwachung, potenzielle Gesundheitsbedenken
- Werte 70-85 zeigen normalen Gesundheitsstatus
- Werte 85-100 repräsentieren ausgezeichnete Gesundheit und optimale Bedingungen
- Gesundheitswerte sind dynamisch und aktualisieren basierend auf jüngster Aktivität und Sensorwerten

Best Practices für Kälbergruppierung:
- Gruppieren Sie Kälber nach Lebenszyklus-Stadium (Neugeboren, vor Absetzen, Absetzphase, nach Absetzen, Aufzucht)
- Organisieren nach Alterskohorten für bessere Gesundheitsüberwachung
- Trennen Sie kranke oder genesende Kälber in dedizierte Gruppen
- Verwenden Sie standortbasierte Gruppierung für Stallabschnittsverwaltung
- Beibehalten konsistenter Gruppenzuweisungen zur Verfolgung von Mustern über Zeit

Aktivitätsüberwachung:
- Normale Aktivitätsmuster variieren nach Alter, Rasse und Lebenszyklus-Stadium
- Plötzliche Aktivitätsabnahmen können Krankheit, Verletzung oder Stress anzeigen
- Erhöhte Unruhe kann Unbehagen, Schmerzen oder Umweltprobleme signalisieren
- Überwachen Sie Fressmuster - reduzierte Milch-/Futteraufnahme ist ein früher Gesundheitsindikator
- Verfolgen Sie Aktivitätstrends über Zeit zur Identifikation subtiler Veränderungen vor klinischen Zeichen
`
                },
                healthIssues: {
                        title: "Häufige Kälber-Gesundheitsprobleme und Symptome",
                        content: `
Frühwarnzeichen:
- Gesundheitswert fällt unter 35 (Verdachtsflagge ausgelöst)
- Reduzierte Aktivität im Vergleich zu Basislinienmustern
- Verringerte Milch-/Futteraufnahme oder Wasserkonsum
- Isolation von der Gruppe oder Widerstand gegen Bewegung
- Abnormale Haltung oder Gang (Lahmheitsindikatoren)
- Veränderungen in Atemmustern oder Husten
- Hängende Ohren, Nasenausfluss, tränende Augen

Bovine Respiratory Disease (BRD):
- Häufigste und kostspieligste Kälberkrankheit
- BioCV KI kann Atemprobleme durch Aktivitätsmusteränderungen erkennen
- Wisconsin-Scoring: Augen (0-3), Ohren (0-3), Nase (0-3), Husten (0-3)
- Wisconsin-Gesamtscore ≥5 = BRD-Behandlung erforderlich
- Frühe Intervention verbessert Ergebnisse signifikant

Neugeborenendurchfall (Kälberdurchfall):
- Häufigste Todesursache bei Kälbern unter 30 Tagen
- California Faecal Scoring: 0 (normal) bis 3 (wässrig/schwer)
- Score ≥2 erfordert Intervention (Flüssigkeitstherapie, Elektrolyte)
- Dehydrierung ist die primäre Todesursache - frühzeitige Rehydrierung entscheidend

Nabelentzündung / Gelenkentzündung:
- Infektion über den Nabel in den ersten Lebenstagen
- Geschwollener, heißer oder nässender Nabel; geschwollene Gelenke; Fieber
- Prävention: Nabeldesinfektion mit Jod direkt nach der Geburt, saubere Abkalbeumgebung

Hitzestress-Indikatoren:
- Erhöhte Körpertemperatur (Sensorwerte über Normalbereich von 38,5-39,5°C)
- Erhöhte Atemfrequenz (>40 Atemzüge/Min)
- Reduzierte Futteraufnahme und Milchkonsum
- Lethargie oder verminderte Aktivität
- Kälber suchen Schatten oder Wasserquellen
`
                },
                treatmentProtocols: {
                        title: "Behandlungsprotokolle und Management",
                        content: `
Behandlungsplanung:
- Konsultieren Sie immer einen Tierarzt für Diagnose und Behandlungsprotokolle
- Verwenden Sie Behandlungsvorlagen für Konsistenz und Compliance
- Dokumentieren Sie alle Behandlungen mit genauen Daten, Dosierungen und Verabreichungswegen
- Setzen Sie Erinnerungen für Nachbehandlungen oder Kontrollen
- Verfolgen Sie Behandlungsergebnisse zur Bewertung der Wirksamkeit

Medikamenten-Best Practices:
- Berechnen Sie Dosierungen basierend auf genauen aktuellen Gewichten
- Befolgen Sie Wartezeiten für Lebensmittel produzierende Tiere
- Führen Sie Behandlungsaufzeichnungen für regulatorische Compliance
- Rotieren Sie Medikamente zur Verhinderung von Resistenzentwicklung
- Lagern Sie Medikamente gemäß Herstellerspezifikationen

Behandlungsplanung:
- Priorisieren Sie überfällige Behandlungen sofort
- Planen Sie Behandlungen während routinemäßiger Stallkontrollen wenn möglich
- Gruppieren Sie Behandlungen nach Standort zur Effizienzsteigerung
- Setzen Sie wiederkehrende Behandlungen für Präventivpflege (Impfungen, Entwurmung)
- Koordinieren Sie mit Arbeitsplaner für Personaleinsatzplanung

Notfallreaktion:
- Kälber mit Gesundheitswerten unter 30 benötigen sofortige Aufmerksamkeit
- Mehrere Kälber mit plötzlichen Wertabfällen können Umweltproblem oder Ausbruch anzeigen
- Koordinieren Sie mit Tierarzt für kritische Fälle
- Isolieren Sie betroffene Kälber zur Verhinderung von Krankheitsausbreitung
- Dokumentieren Sie Notfallbehandlungen und Ergebnisse
`
                },
                breedingCycle: {
                        title: "Kälber-Lebenszyklus-Management",
                        content: `
Neugeborenen-Phase (0-3 Tage):
- Kolostrummanagement ist der wichtigste Faktor für das Kälberüberleben
- Erste Fütterung innerhalb von 2 Stunden nach Geburt, mindestens 4 Liter Qualitätskolostrum
- Kolostrumqualität mit Brix-Refraktometer messen (>22% Brix = gute Qualität)
- Nabeldesinfektion mit 7% Jodlösung sofort nach der Geburt
- Passive Transferkontrolle: Serum-Gesamtprotein >5,5 g/dL nach 24-48 Stunden

Vor-Absetz-Phase (3 Tage - 8 Wochen):
- Konsistenter Milch-/Milchaustauscherfütterungsplan (2-3 mal täglich)
- Kälberstarter ab Tag 3-4 einführen zur Pansenentwicklung
- Tägliche Überwachung auf Durchfall, Atemsymptome und allgemeine Wachheit
- Ziel-Tageszunahme (ADG): 0,7-1,0 kg/Tag
- Frisches Wasser jederzeit verfügbar ab Tag 1

Absetz-Phase (6-12 Wochen):
- Schrittweises Absetzen wenn Kalb regelmäßig 1-1,5 kg Starter frisst
- Stufenweises Absetzen über 7-14 Tage bevorzugt
- Gesundheitswerte können während Absetzübergang vorübergehend sinken
- Auf anhaltende Rückgänge vs. normale Anpassung achten

Aufzucht-Phase (12+ Wochen):
- Übergang zu Aufzuchtration
- Gruppenhaltung mit angemessener Besatzdichte
- Wachstumsrate weiter überwachen (Ziel-ADG basierend auf Rassezielen)
- Impfauffrischungen gemäß Protokoll
- Regelmäßige Gewichtserfassung für Wachstumskurvenverfolgung
`
                },
                bionodeTroubleshooting: {
                        title: "BioNode Fehlerbehebung",
                        content: `
Verbindungsprobleme:
- Überprüfen Sie BioNode-Status mit bionode-status Tool zur Verbindungsverifikation
- Knoten, die "getrennt" oder "offline" zeigen, erfordern Untersuchung
- Verifizieren Sie Netzwerkverbindung und Stromversorgung
- Überprüfen Sie Bluetooth-Verbindung bei drahtloser Kommunikation
- Starten Sie BioNode neu, wenn Verbindungsprobleme bestehen bleiben

Datensammlungsprobleme:
- Fehlende oder verzögerte Sensorwerte können Sensorfehlfunktion anzeigen
- Überprüfen Sie Tieraktivitätsdaten - Lücken deuten auf Kommunikationsprobleme
- Verifizieren Sie, dass BioNode in Reichweite von Tiertranspondern ist
- Überprüfen Sie auf physische Hindernisse, die Signalübertragung beeinträchtigen
- Überprüfen Sie kürzliche Firmware-Updates oder Konfigurationsänderungen

Sensorgenauigkeit:
- Temperatur- und Feuchtigkeitswerte sollten Umgebungsbedingungen entsprechen
- Unerwartete Sensorwerte können Kalibrierungsprobleme anzeigen
- Vergleichen Sie Werte über mehrere Knoten für Konsistenz
- Verifizieren Sie Sensorplatzierung und Umweltfaktoren
- Dokumentieren Sie persistierende Genauigkeitsprobleme für technischen Support

Diagnoseschritte:
1. Verwenden Sie bionode-analysis Tool für detaillierte Knotendiagnostik
2. Überprüfen Sie Verbindungsstatus und letzte Datenübertragungszeit
3. Überprüfen Sie kürzliche Fehlerprotokolle oder Warnmeldungen
4. Verifizieren Sie Stromversorgung und Netzwerkverbindungen
5. Testen Sie Kommunikation mit spezifischen Tiertranspondern
6. Kontaktieren Sie technischen Support, wenn Probleme nach grundlegender Fehlerbehebung bestehen bleiben
`
                },
                featureCapabilities: {
                        title: "BioCV CalfHealth Feature-Funktionen",
                        content: `
Tierverwaltung:
- Echtzeit-Tracking einzelner Kälber nach ID oder MAC-Adresse
- Gruppenverwaltung mit Bulk-Operations-Unterstützung
- Gesundheitswertüberwachung mit automatischer Verdachtskennzeichnung
- Standortverfolgung innerhalb von Stallabschnitten
- Vollständige Lebenszyklusdokumentation von Geburt durch alle Stadien

Kälbergesundheits-Tools:
- Lebenszyklus-Übersicht: Kälber über alle Stadien verfolgen (Neugeboren → Vor-Absetzen → Absetzen → Nach-Absetzen → Aufzucht → Endmast)
- Impfstatus: Impf-Compliance überwachen, überfällige und anstehende Impfungen
- Gewichts- & Wachstumsanalyse: ADG (Tageszunahme) verfolgen, Gewichtsanomalien erkennen
- Krankheitsübersicht: Krankheitsaufzeichnungen nach Kategorie, Wisconsin-Scores, California-Scores
- Kälbergesundheits-KPIs: Mortalitätsrate, ADG, Impf-Compliance, Krankheitsinzidenz, Durchfallrate, BRD-Rate

Behandlungsmanagement:
- Behandlungsvorlagen für gängige Protokolle
- Planungssystem mit automatisierten Erinnerungen
- Überfällige Behandlungsalarme und Priorisierung
- Vollständige Behandlungsgeschichte pro Kalb
- Compliance-Berichterstattung und Dokumentation

Arbeitsplanung:
- Kalender-, Kanban- und Listenansichten für Aufgabenmanagement
- Mitarbeiterzuweisung und Aufgabenpriorisierung
- Wiederkehrende Aufgabenplanung
- Aufgabenabhängigkeiten und Sequenzierung
- Mobile Benachrichtigungen für zugewiesene Aufgaben

Sensor-Integration:
- Temperatur- und Feuchtigkeitsüberwachung (Silo-Sensoren, Umgebungssensoren)
- Wasserkonsumtracking (BioFlow-Wasserzähler)
- Echtzeit-Alarme für Schwellenwertüberschreitungen
- Historische Datenvisualisierung und Trendanalyse

Verfügbare Datentools:
- Verwenden Sie account-overview für Benutzerprofilinformationen
- Verwenden Sie treatment-history für aktive und vergangene Behandlungen
- Verwenden Sie birth-history für Geburts-/Ankunftsaufzeichnungen und Lebenszyklus-Übergänge
- Verwenden Sie calf-lifecycle-overview für Stadienverteilung in der Herde
- Verwenden Sie vaccination-status für Impf-Compliance-Tracking
- Verwenden Sie weight-growth-analysis für ADG und Wachstumsüberwachung
- Verwenden Sie disease-overview für Krankheitsanalyse und Wisconsin-Scores
- Verwenden Sie calf-health-kpis für umfassende Herdengesundheits-KPIs
- Verwenden Sie work-planner-overview für Aufgabenmanagement
- Verwenden Sie animal-details für umfassende Kälberinformationen
- Verwenden Sie bionode-status und bionode-analysis für Gerätediagnostik
- Verwenden Sie farm-kpi-overview für KPI-Schnappschüsse des Bestands
- Verwenden Sie farm-kpi-trends zum Vergleichen von KPI-Veränderungen
`
                },
                productionData: {
                        title: "Kälber-Produktionsdaten Referenz",
                        content: `
Umfang und Datentypen:
- Vollständige Abbildung des Kälber-Lebenszyklus von Geburt/Ankunft durch alle Wachstumsstadien
- ERP-Sammlungen umfassen Geburten, Lebenszyklus-Ereignisse, Impfungen, Gewichtsaufzeichnungen, Krankheitsaufzeichnungen, Behandlungen und Allgemeine Ereignisse
- Lebenszyklus-Stadien: Neugeboren, Vor-Absetzen, Absetzen, Nach-Absetzen, Aufzucht, Endmast, Verkauft, Verstorben

Wichtige Felder:
- Geburtsaufzeichnungen: Datum, Mutter-ID, Geburtsgewicht, Geschlecht, Kalbungsschwierigkeit, Kommentare
- Lebenszyklus-Ereignisse: Stadienübergänge mit Zeitstempeln und Notizen
- Gewichtsaufzeichnungen: Wachstum über Zeit, ermöglicht ADG-Berechnung und Anomalie-Erkennung
- Impfaufzeichnungen: Protokoll-Compliance, Fälligkeitsdaten, Verabreichungsdetails
- Krankheitsaufzeichnungen: Wisconsin-Scores, California-Scores, Diagnosen und Ergebnisse

CSV-Import Ablauf:
1. CSV hochladen und Datentyp wählen (Delimiter, Encoding und Header werden automatisch erkannt)
2. Pflichtspalten zuordnen mit intelligenten Vorschlägen
3. Transformierte Zeilen prüfen inkl. Validierung
4. Import in Batches mit Fortschrittsanzeige

Praxis-Tipps:
- Tiere vor dem Import anlegen und IDs vereinheitlichen
- Unterstützte Datumsformate (ISO, US, EU) verwenden
- Große historische Datensätze in Pakete von ca. 200 Zeilen teilen
- Geburtsgewichte, Muttertier-Informationen und Kalbungsschwierigkeit erfassen
`
                },
                operationalGuidance: {
                        title: "Operative Anleitung",
                        content: `
Tägliche Betriebs-Checkliste:
1. Überprüfen Sie Prioritätsalarme und verdächtige Kälber (Gesundheitswerte < 35)
2. Überprüfen Sie überfällige Behandlungen und Impfungen, planen Sie dringende Interventionen
3. Neugeborene Kälber auf Kolostrumaufnahme und Vitalität überwachen
4. Auf Durchfallzeichen bei Kälbern vor dem Absetzen prüfen
5. Überprüfen Sie Arbeitsplaner für heute geplante Aufgaben
6. Überprüfen Sie Sensorwerte für Umweltanomalien
7. Verifizieren Sie BioNode-Verbindung und Datensammlung

Wöchentliche Überprüfung:
- Analysieren Sie Gesundheitswerttrends über Herde
- Überprüfen Sie Behandlungsergebnisse und passen Sie Protokolle nach Bedarf an
- Impf-Compliance bewerten und anstehende Impfungen planen
- Beurteilen Sie Gruppenleistung und erwägen Sie Umorganisation
- Gewichtszunahme-Daten und ADG-Trends überprüfen
- Überprüfen Sie Gerätestatus und adressieren Sie Verbindungsprobleme

Monatliche Analyse:
- Generieren Sie Produktionsberichte und Leistungsmetriken (Mortalität, ADG, Krankheitsinzidenz)
- Überprüfen Sie Behandlungskosten und Medikamentenverbrauch
- Wisconsin- und California-Scores für Krankheitstrends analysieren
- Kälber-Wachstumskurven gegen Rasseziele bewerten
- Überprüfen Sie Sensorkalibrierung und Genauigkeit
- Planen Sie für bevorstehende Absetzübergänge und Gruppenbewegungen

Datengetriebene Entscheidungsfindung:
- Begründen Sie Empfehlungen immer mit tatsächlichen Farmdaten
- Verwenden Sie Tool-Aufrufe zur Abfrage präziser Informationen vor Beratung
- Vergleichen Sie aktuelle Daten mit historischen Mustern
- Identifizieren Sie Trends, bevor sie Probleme werden
- Nutzen Sie Gesundheitsüberwachungsdaten zur Optimierung des Zeitpunkts von Interventionen
`
                },
                diseaseProfiles: {
                        title: "Kälberkrankheiten - Diagnostik und Management",
                        content: `
ATEMWEGSERKRANKUNGEN:

Bovine Respiratory Disease Complex (BRD):
- Häufigste und wirtschaftlich bedeutsamste Kälberkrankheit
- Erreger: Mannheimia haemolytica, Pasteurella multocida, Histophilus somni, BRSV, IBR, PI3, BVD
- Symptome: Husten, Nasenausfluss, Fieber (>39,5°C), hängende Ohren, reduzierte Futteraufnahme, Depression
- Sensormuster: Plötzlicher Aktivitätsabfall (30-50% unter Ausgangswert), reduzierte Fressaktivität, Isolation
- Wisconsin-Scoring-System:
  * Augen: 0=normal, 1=leichter Ausfluss, 2=mäßig bilateral, 3=starker Ausfluss/Verkrustung
  * Ohren: 0=normal, 1=Ohrenschütteln, 2=einseitig hängend, 3=beidseitig hängend
  * Nase: 0=normal, 1=gering einseitig, 2=bilateral/trüb, 3=reichlich mukopurulent
  * Husten: 0=keiner, 1=einzeln induziert, 2=wiederholt spontan, 3=wiederholt feucht
  * Gesamt ≥5 = BRD behandeln
- Maßnahme: Isolieren, antimikrobielle Behandlung nach Tierarztprotokoll, NSAID bei Fieber/Schmerzen
- Prävention: Gute Belüftung, Kolostrummanagement, Impfprogramm, Überbelegung vermeiden

VERDAUUNGSSTÖRUNGEN:

Neugeborenendurchfall (Kälberdurchfall):
- Häufigste Todesursache bei Kälbern unter 30 Tagen
- Ursachen nach Alter:
  * Tag 1-3: E.coli (enterotoxigen), Clostridium perfringens
  * Tag 3-21: Rotavirus, Coronavirus, Cryptosporidium
  * Tag 7-28: Cryptosporidium, Kokzidien, Salmonellen
- California Faecal Scoring:
  * 0 = Normal (fest aber nicht hart)
  * 1 = Halbgeformt, breiig
  * 2 = Lose, bleibt auf Oberfläche
  * 3 = Wässrig, sickert durch Oberfläche
  * Score ≥2 = Intervention nötig
- Maßnahme: Orale Elektrolyte (Milchfütterung NICHT stoppen), IV-Flüssigkeiten bei schwerer Dehydrierung
- Prävention: Richtige Kolostrumaufnahme, saubere Abkalbeumgebung, Mutterkuh-Impfung

NABEL- UND GELENKINFEKTIONEN:

Nabelentzündung (Omphalitis):
- Infektion des Nabels, meist in der ersten Lebenswoche
- Symptome: Geschwollener, heißer oder nässender Nabel; Fieber; schlechtes Saugen
- Kann führen zu: Leberabszess, Gelenkentzündung, Septikämie
- Prävention: Nabel mit 7% Jod nach Geburt desinfizieren, sauberer trockener Abkalbebereich

KRANKHEITSÜBERWACHUNG MIT BIOCV:

Frühwarnindikatoren:
1. Gruppen-Gesundheitswert sinkt >5 Punkte Durchschnitt in 24 Stunden = untersuchen
2. 3+ Kälber am gleichen Ort zeigen gleichzeitige Veränderungen = potenzieller Ausbruch
3. Aktivitätsmuster weichen >30% vom Gruppenausgangswert ab = individuelle Aufmerksamkeit nötig
4. Umgebungssensoren zeigen Temperatur-/Feuchtigkeitsextreme = Stressrisiko

WANN SOFORT DEN TIERARZT RUFEN:
- Mehrere Kälber mit plötzlichen schweren Symptomen
- Gesundheitswerte unter 30 mit schnellem Rückgang
- Schwere Dehydrierung (eingesunkene Augen, Hautfaltenttest >3 Sekunden)
- Neurologische Symptome (Kreisen, Zittern, Lähmung)
- Plötzliche Todesfälle ohne offensichtliche Ursache
- Verdacht auf meldepflichtige Krankheit
`
                },
                nutritionGuidance: {
                        title: "Kälber-Ernährung und Fütterungsmanagement",
                        content: `
FÜTTERUNG NACH LEBENSZYKLUS-STADIUM:

Neugeborene (Tag 0-3):
- Kolostrum ist die #1 Priorität
  * Erste Fütterung: 4 Liter innerhalb von 2 Stunden nach Geburt (10% des Körpergewichts)
  * Zweite Fütterung: 2-3 Liter innerhalb von 6-12 Stunden
  * Qualität: >22% Brix-Refraktometer = gute Qualität
  * Passive Transferkontrolle: Serum-Gesamtprotein >5,5 g/dL nach 24-48 Stunden
- Fehlgeschlagener passiver Transfer erhöht Krankheitsrisiko 3-5x

Vor-Absetz-Phase (Tag 3 bis 6-8 Wochen):
- Vollmilch oder Milchaustauscher (MAT):
  * 4-6 Liter/Tag aufgeteilt auf 2-3 Fütterungen
  * MAT: mindestens 20% Protein, 20% Fett
  * Intensive Programme: 26-28% Protein, 15-20% Fett, 6-8 Liter/Tag
- Kälberstarter: Einführung ab Tag 3-4
  * 18-20% Rohprotein
  * Ziel: 1-1,5 kg/Tag Starteraufnahme vor dem Absetzen
- Frisches sauberes Wasser: Verfügbar ab Tag 1
- Ziel-ADG: 0,7-1,0 kg/Tag (intensiv: 0,9-1,2 kg/Tag)

Absetz-Übergang (6-12 Wochen):
- Absetzbereit wenn 1-1,5 kg Starter konsistent über 3 aufeinanderfolgende Tage
- Stufenweise Methode: Milch schrittweise über 7-14 Tage reduzieren
- Gewicht und Gesundheit während Übergang genau überwachen

ERNÄHRUNGSBEZOGENE GESUNDHEITSINDIKATOREN:

Futteraufnahme-Überwachung:
- Reduzierte Aufnahme ist oft das ERSTE Krankheitszeichen
- BioFlow Wasserzähler helfen Verbrauchsmuster zu verfolgen
- Plötzliche Aufnahmerückgänge (>30%) erfordern Untersuchung

WASSERBEDARF:

Täglicher Wasserbedarf:
- Neugeborene Kälber: 0,5-1 Liter/Tag (zusätzlich zur Milch)
- Vor-Absetz-Kälber: 2-5 Liter/Tag
- Absetz-Kälber: 5-10 Liter/Tag
- Nach-Absetz: 10-20+ Liter/Tag (5-10% des Körpergewichts)
- Laktierende Kühe: 70-120+ Liter/Tag
`
                },
                economicAnalysis: {
                        title: "Wirtschaftsanalyse und Kälbergesundheits-KPIs",
                        content: `
WICHTIGE LEISTUNGSKENNZAHLEN (KPIs):

Kälber-Mortalitätsrate:
- Definition: (Todesfälle / Gesamtzahl geborener oder aufgenommener Kälber) × 100
- Ziel-Benchmarks:
  * Vor Absetzen: <5% (Elite: <3%)
  * Nach Absetzen: <2%
  * Gesamt: <5%
- Hohe Mortalität zeigt an: schlechtes Kolostrummanagement, Krankheitsdruck, Umweltprobleme

Durchschnittliche Tageszunahme (ADG):
- Definition: (Aktuelles Gewicht - Geburtsgewicht) / Alter in Tagen
- Ziel-Benchmarks nach Stadium:
  * Vor Absetzen (Vollmilch): 0,7-1,0 kg/Tag
  * Vor Absetzen (intensiv): 0,9-1,2 kg/Tag
  * Nach Absetzen: 0,8-1,2 kg/Tag
- Niedriger ADG zeigt an: Krankheit, schlechte Ernährung, Stress

Impf-Compliance:
- Definition: (Rechtzeitig geimpft / Gesamt fällig) × 100
- Ziel: >95%
- Niedrige Compliance erhöht Krankheitsrisiko und Behandlungskosten

Krankheitsinzidenzrate:
- Definition: (Neue Krankheitsfälle / Gesamtzahl Kälber) × 100 in einer Periode
- Nach Krankheitskategorie verfolgen: respiratorisch (BRD), digestiv (Durchfall), andere
- Ziel: <15% BRD-Inzidenz, <20% Durchfall-Inzidenz bei Kälbern vor dem Absetzen

Durchfallrate:
- Definition: (Kälber mit California Faecal Score ≥2 / Gesamtzahl) × 100
- Ziel: <15% bei Kälbern vor dem Absetzen

BRD-Rate:
- Definition: (Kälber mit Wisconsin Score ≥5 / Gesamtzahl) × 100
- Ziel: <10% (Elite: <5%)

KOSTENANALYSE:

Behandlungskosten:
- Behandlungskosten pro Kalb pro Periode verfolgen
- Präventionsprogramme sind fast immer wirtschaftlicher als Behandlung

Mortalitätskosten:
- Jeder Kälbertod = gesamte Investition verloren (Genetik, Kolostrum, Futter, Arbeit, Behandlungen)
- Geschätzter Wert pro Kalb beim Absetzen: 200-500+ EUR je nach Rasse/Genetik

BERICHTS- UND ANALYSE-TOOLS:

generate-report Tool verwenden für:
- weekly-summary: Schneller Leistungsüberblick
- health-overview: Fokus auf Gesundheitsmetriken und Bedenken
- calf-health: Impf-, Krankheits- und Wachstumsleistungsübersicht
- vet-prep: Umfassende Zusammenfassung für Tierarztbesuche

comparative-analysis Tool verwenden um:
- Gruppenleistung vergleichen (z.B. Vor-Absetz vs. Nach-Absetz Kohorten)
- Gegen vorherige Perioden benchmarken
- Beste und schlechteste Kohorten identifizieren
`
                },
                troubleshootingDecisions: {
                        title: "Fehlerbehebungs-Entscheidungsbäume",
                        content: `
ENTSCHEIDUNGSBAUM: Kalb mit niedrigem Gesundheitswert (<35)

Schritt 1: Rückgangsmuster bewerten
├── PLÖTZLICHER ABFALL (>20 Punkte in 24 Stunden)?
│   ├── JA → Auf akute Erkrankung prüfen
│   │   ├── Mehrere Kälber gleichzeitig betroffen?
│   │   │   ├── JA → Wahrscheinlich Umwelt- oder Infektionsausbruch
│   │   │   │   ├── Prüfen: Belüftung, Temperatur, Wasser, Futterqualität
│   │   │   │   ├── Prüfen: Kürzliche Gruppenzugänge (Krankheitseinschleppung)
│   │   │   │   └── MASSNAHME: Tierarzt kontaktieren, Gruppe isolieren, Umgebung untersuchen
│   │   │   └── NEIN → Individuelles akutes Problem
│   │   │       ├── Prüfen: Durchfall (California Score), Atemsymptome (Wisconsin Score)
│   │   │       └── MASSNAHME: Kalb untersuchen, Behandlungshistorie prüfen, Tierarzt konsultieren
│   └── NEIN → Allmählicher Rückgang
│       ├── Behandlungshistorie auf chronische Erkrankung prüfen
│       ├── Aktivitätsmuster auf Verhaltenshinweise prüfen
│       └── MASSNAHME: Untersuchung planen, Historie prüfen, genau überwachen

ENTSCHEIDUNGSBAUM: Kalb mit Durchfall

Schritt 1: Schweregrad bewerten (California Faecal Score)
├── Score 0-1 (Normal/Mild)?
│   └── Genau überwachen, ausreichende Wasseraufnahme sicherstellen
├── Score 2 (Lose)?
│   ├── Dehydrierungsstatus prüfen (Hautfaltentest, Augenerscheinung)
│   ├── Orale Elektrolyte zwischen Milchfütterungen beginnen
│   ├── Milchfütterung NICHT stoppen
│   └── MASSNAHME: 2x täglich überwachen, Ursache behandeln
└── Score 3 (Wässrig/Schwer)?
    ├── Auf schwere Dehydrierung prüfen (eingesunkene Augen, Hautfalte >3 Sek)
    ├── IV-Flüssigkeiten bei schwerer Dehydrierung (tierärztliche Hilfe)
    └── MASSNAHME: Tierarzt kontaktieren, Kalb isolieren, intensive Überwachung

ENTSCHEIDUNGSBAUM: BioNode-Verbindungsprobleme

Schritt 1: Umfang des Problems
├── EINZELNER KNOTEN betroffen?
│   ├── Stromversorgung prüfen
│   ├── Physischen Standort prüfen
│   ├── Netzwerkindikatoren prüfen
│   └── MASSNAHME: Knoten aus-/einschalten, Kabel prüfen, bei Bedarf umplatzieren
│
└── MEHRERE KNOTEN betroffen?
    ├── Gemeinsame Infrastruktur prüfen
    ├── Auf kürzliche Änderungen prüfen
    └── MASSNAHME: Netzwerkausrüstung neustarten, Gateway prüfen, Support kontaktieren

Verwenden Sie bionode-status und bionode-analysis Tools zur Sammlung von Diagnosedaten vor der Fehlerbehebung.

ALLGEMEINER FEHLERBEHEBUNGSANSATZ:

1. ZUERST DATEN SAMMELN
   - Relevante Tools verwenden um aktuelle Informationen zu erhalten
   - Nicht annehmen - mit Daten verifizieren

2. NACH MUSTERN SUCHEN
   - Ist es individuell oder gruppenweit?
   - Ist es plötzlich oder allmählich?
   - Ist es neu oder wiederkehrend?

3. DAS OFFENSICHTLICHE PRÜFEN
   - Umgebung (Temperatur, Belüftung, Wasser, Einstreu)
   - Kürzliche Änderungen (Futter, Management, Personal, Gruppenbewegungen)
   - Ausrüstungsfunktion

4. ANGEMESSEN ESKALIEREN
   - Managementprobleme → Protokolle anpassen
   - Gesundheitsbedenken → Tierärztliche Beratung
   - Ausrüstungsprobleme → Technischer Support

5. DOKUMENTIEREN UND NACHVERFOLGEN
   - Ergebnisse und Maßnahmen aufzeichnen
   - Nachfolgeprüfungen planen
   - Interventionserfolg bewerten
`
                }
        }
};

/**
 * Gets knowledge sections for a given language
 * @param {string} language - Language code (e.g., 'en', 'de')
 * @returns {Object} Knowledge base object for the language
 */
export function getKnowledgeBase(language = "en") {
        const langCode = (language?.startsWith("de") || language === "ger") ? "de" : "en";
        return KNOWLEDGE_BASE[langCode] || KNOWLEDGE_BASE.en;
}

/**
 * Formats knowledge base sections into a formatted string for prompt inclusion
 * @param {string} language - Language code
 * @param {Array<string>} sections - Optional array of section keys to include. If omitted, includes all sections.
 * @returns {string} Formatted knowledge base content
 */
export function formatKnowledgeBase(language = "en", sections = null) {
        const knowledge = getKnowledgeBase(language);
        const sectionsToInclude = sections || Object.keys(knowledge);

        const formattedSections = sectionsToInclude
                .filter(key => knowledge[key])
                .map(key => {
                        const section = knowledge[key];
                        return `${section.title}:\n${section.content.trim()}`;
                });

        if (formattedSections.length === 0) {
                return "";
        }

        return `\n\nKnowledge Base:\n${formattedSections.join("\n\n")}`;
}

/**
 * Gets a specific knowledge section
 * @param {string} sectionKey - Key of the section to retrieve
 * @param {string} language - Language code
 * @returns {Object|null} Knowledge section object or null if not found
 */
export function getKnowledgeSection(sectionKey, language = "en") {
        const knowledge = getKnowledgeBase(language);
        return knowledge[sectionKey] || null;
}
